import React, { FormEvent, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Autocomplete, Box, Button, Stack, TextField, Typography } from '@mui/material';

import { searchTypeahead } from '~/services/SearchService';

type Props = {
  onClose: () => void;
  onLocationSelected: (placeId: string, locationName: string) => void;
};

interface LocationOption {
  placeId: string;
  name: string;
  mainText: string;
  secondaryText?: string;
  searchType: string;
}

const SEARCH_TYPES = [
  'airport',
  'province_state',
  'neighborhood',
  'city',
  'high_level_region',
  'country',
  'multi_city_vicinity',
  'metro_station',
  'continent',
  'train_station',
  'point_of_interest',
  'colloquial_area',
];

const LocationSelectForm = ({ onClose, onLocationSelected }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState<Array<LocationOption>>([]);
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (query: string) => {
    if (!query) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await searchTypeahead(query, {
        types: SEARCH_TYPES,
        brand: 'luxuryescapes',
        region: 'AU',
        limit: 20,
        priority: 'current',
      });

      if (response.status === 200 && response.result) {
        const locations = response.result.map((item: any) => ({
          placeId: item.id,
          name: item.name,
          mainText: item.format.mainText,
          secondaryText: item.format.secondaryText,
          searchType: item.searchType,
        }));
        setOptions(locations);
      }
    } catch (error) {
      console.error('Failed to search locations:', error);
      enqueueSnackbar('Failed to search locations', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (selectedLocation) {
      onLocationSelected(selectedLocation.placeId, selectedLocation.name);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <Autocomplete
          options={options}
          getOptionLabel={(option) => option.mainText}
          loading={loading}
          value={selectedLocation}
          onChange={(_, newValue) => setSelectedLocation(newValue)}
          onInputChange={(_, newValue) => {
            setSearchTerm(newValue);
            handleSearch(newValue);
          }}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Stack>
                <Typography variant="body1">{option.mainText}</Typography>
                {option.secondaryText && (
                  <Typography variant="caption" color="text.secondary">
                    {option.secondaryText}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Type: {option.searchType}
                </Typography>
              </Stack>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search Location"
              required
              fullWidth
              value={searchTerm}
              placeholder="Type to search locations..."
            />
          )}
        />
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary" disabled={!selectedLocation}>
            Select Location
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default LocationSelectForm;
